import React, { useEffect, useState } from 'react';
import first from 'lodash/first';
import styled from 'styled-components';

import isEmpty from 'lodash/isEmpty';
import { DateTimeService } from '@shared/services/DateTimeService';
import { SlotType } from '@shared/types/SlotType';
import arrow_left from '../../images/arrow-left.svg';
import arrow_right from '../../images/arrow-right.svg';
import EmptyText from '../empty/EmptyText';
import media from '../../utils/mediaQueries';
import { LINKS_COLOR, TEXT_COLOR } from '../../theme';

const CalendarContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 -4px 5px;
  width: 100%;
  ${media.mobile`
    margin: 0 -9px;
    flex-direction: column;
    width: calc(100% + 18px);
  `}
`;

const NavButton = styled.div`
  top: 15px;
  ${(props) => (props.direction === 'left' ? 'left: -24px;' : 'right: -24px;')}
  ${media.mobile`
    ${(props) => (props.direction === 'left' ? 'left: 0;' : 'right: 0;')}
    top: 20px;
  `}
  width: 24px;
  height: 24px;
  border-radius: 50%;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
  background-color: #597cbd;
  display: none;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    opacity: 0.8;
  }
  ${(props) => props.isNavVisible
    && `
    display: flex;
  `}
`;

const Image = styled.img`
  width: 16px;
  height: 16px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Column = styled.div`
  margin: 0 ${(props) => props.columnGap}px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  ${media.mobile`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
    gap: 3px 6px;
    margin: 8px 0 0;
  `}
`;

const Slot = styled.div`
  margin-bottom: 10px;
  padding: 10px 0;
  width: 64px;
  border-radius: 6px;
  border: solid 1px #a6c8f0;
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  letter-spacing: -0.2px;
  color: ${(props) => (props.active ? '#ffffff' : LINKS_COLOR)};
  ${(props) => props.active
    ? `
    font-weight: bold;
    background-color: ${LINKS_COLOR};
    border-color: ${LINKS_COLOR};
  `
    : `
    &:hover {
      border-color: ${LINKS_COLOR};
      font-weight: bold;
    }
  `}
  transition: all .2s;
  ${media.mobile`
    width: auto;
  `}
`;

const SlotHeader = styled.div<{
  isSelected?: boolean;
}>`
  font-size: 14px;
  line-height: 1.29;
  letter-spacing: 0.1px;
  text-align: center;
  color: ${TEXT_COLOR};
  margin-bottom: 8px;
  cursor: pointer;
  padding: 10px 8px;
  border-radius: 12px;
  min-width: 70px;
  ${({ isSelected }) => (isSelected ? 'font-weight: bold;' : '')};
  ${({ isSelected }) => (isSelected ? 'background-color: #f4f7f9' : '')};
  ${media.mobile`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    font-size: 16px;
    flex-direction: column;
    line-height: 1.5;
    letter-spacing: 0.2px;
    > div:first-child {
      margin-right: 4px;
    }
  `}
`;

const SlotHeaderContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

type SeparateSlotsProps = {
  onClickNavLeft: () => void;
  isLeftNavVisible: boolean;
  onClickNavRight: () => void;
  isRightNavVisible: boolean;
  slots: any[];
  onSlotClick: (slot: SlotType) => void;
  timeZoneName: string;
  emptyText: string;
  columnGap: number;
  selectedSlot: any;
  localPage: number;
  maxLocalPages: number;
};

const SeparateSlots = (props: SeparateSlotsProps) => {
  const {
    onClickNavLeft,
    isLeftNavVisible,
    onClickNavRight,
    isRightNavVisible,
    slots,
    onSlotClick,
    timeZoneName,
    emptyText,
    columnGap = 8,
    selectedSlot,
    localPage,
    maxLocalPages,
  } = props;

  const [selectedDate, setSelectedDate] = useState(null);

  const renderSlotHeader = (slot) => {
    const slotStartDate = new Date(slot.start_date);
    const { setDateLocale } = DateTimeService;
    const isSlotHeaderSelected = selectedDate
      && new Date(selectedDate.start_date).toISOString() === slotStartDate.toISOString();

    return (
      <SlotHeader isSelected={isSlotHeaderSelected} onClick={() => setSelectedDate(slot)}>
        <div>{setDateLocale(slotStartDate, 'EEEE')}</div>
        <div>{setDateLocale(slotStartDate, 'dd MMM')}</div>
      </SlotHeader>
    );
  };

  const slotsArray = Object.entries(slots).slice(
    localPage * maxLocalPages,
    (localPage + 1) * maxLocalPages,
  );

  useEffect(() => {
    if (!selectedDate && !isEmpty(slotsArray)) {
      setSelectedDate(slotsArray[0][1][0]); // select first available slot
    }
  }, [slotsArray]);

  const handleRightNavClick = () => {
    setSelectedDate(null);
    onClickNavRight();
  };

  const handleLeftNavClick = () => {
    setSelectedDate(null);
    onClickNavLeft();
  };

  return (
    <CalendarContainer>
      {isEmpty(slotsArray) ? (
        <EmptyText text={emptyText} />
      ) : (
        <ContentContainer>
          <SlotHeaderContainer>
            <NavButton
              direction="left"
              onClick={handleLeftNavClick}
              isNavVisible={isLeftNavVisible}
            >
              <Image src={arrow_left} alt="flèche droite" />
            </NavButton>
            {slotsArray.map(([date, column], index) => (
              <div key={`booking-column${index}-${date}`}>
                {first(column) && renderSlotHeader(first(column))}
              </div>
            ))}
            <NavButton
              direction="right"
              onClick={handleRightNavClick}
              isNavVisible={isRightNavVisible}
            >
              <Image src={arrow_right} alt="flèche droite" />
            </NavButton>
          </SlotHeaderContainer>

          <>
            {slotsArray.map(([date, column], index) => {
              const d1 = new Date(date);
              const d2 = selectedDate && new Date(selectedDate.start_date);
              const isSameDate = selectedDate
                && d1.getFullYear() === d2.getFullYear()
                && d1.getMonth() === d2.getMonth()
                && d1.getDate() === d2.getDate();
              if (!isSameDate) return null;
              return (
                <ColumnContainer key={`booking-column${index}`}>
                  <Column columnGap={columnGap}>
                    {column.map((slot, yindex) => (
                      <Slot
                        key={`booking-slot${yindex}`}
                        onClick={() => onSlotClick(slot)}
                        active={
                          selectedSlot?.start_date
                          && new Date(selectedSlot.start_date).toISOString()
                            === new Date(slot.start_date).toISOString()
                        }
                      >
                        {DateTimeService.formatInTimeZone(
                          new Date(slot.start_date),
                          timeZoneName,
                          'HH:mm',
                        )}
                      </Slot>
                    ))}
                  </Column>
                </ColumnContainer>
              );
            })}
          </>
        </ContentContainer>
      )}
    </CalendarContainer>
  );
};

export { SeparateSlots };
